import { ArrowRight, Lock, Radio, Users } from "lucide-react";
import { Link } from "react-router";

import { Spinner } from "@/components/data-state";
import type { Challenge, SimQuota, SimSession } from "@/lib/api";
import { formatCountdown, formatEasternTimestamp, formatRelative } from "@/lib/format";
import { attemptHref } from "@/lib/links";
import { cn } from "@/lib/utils";

/**
 * What is happening right now, if anything: a run of yours in progress, a reservation
 * your SDK has not redeemed, a teammate holding the team's slot -- or, on a competition,
 * the reason you cannot start one yet (no team, or teams still open).
 *
 * Sits above the tiles because it is the one thing on the page that is changing. The
 * student who just typed `run()` in a terminal and got sent here wants the live view one
 * click away, not a tab and a table row away.
 */
export function ActiveAttemptBanner({
  challenge,
  quota,
  sessions,
}: {
  challenge: Challenge;
  quota: SimQuota | null;
  sessions: readonly SimSession[] | null;
}) {
  const running = (sessions ?? []).filter((s) => s.status === "running");
  if (running.length > 0) {
    const oldestFirst = [...(sessions ?? [])].sort((a, b) => a.started_at - b.started_at);
    return (
      <>
        {running.map((s) => (
          <Banner key={s.id} tone="live" to={attemptHref(challenge.id, s.id)}>
            <Radio className="text-primary size-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="font-medium">
                Attempt {oldestFirst.findIndex((x) => x.id === s.id) + 1} is running
              </span>
              <span className="text-muted-foreground">
                {" · "}
                {(s.live?.steps_used ?? s.steps_used).toLocaleString()} of{" "}
                {challenge.max_steps.toLocaleString()} steps · started{" "}
                {formatRelative(s.connected_at ?? s.started_at)}
              </span>
            </span>
            <span className="text-primary flex shrink-0 items-center gap-1 text-sm font-medium">
              Watch live
              <ArrowRight className="size-4" aria-hidden />
            </span>
          </Banner>
        ))}
      </>
    );
  }

  if (quota?.reservation) {
    const r = quota.reservation;
    return (
      <Banner tone="muted" to={attemptHref(challenge.id, r.session_id)}>
        <Spinner className="size-4 shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="font-medium">Session reserved</span>
          <span className="text-muted-foreground">
            {" · "}waiting for your SDK to connect · lapses {formatCountdown(r.expires_at)}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-sm font-medium">
          Open
          <ArrowRight className="size-4" aria-hidden />
        </span>
      </Banner>
    );
  }

  if (quota && quota.running > 0) {
    return (
      <Banner tone="muted">
        <Users className="text-muted-foreground size-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="font-medium">A teammate has a session running</span>
          <span className="text-muted-foreground">
            {" · "}
            {quota.max_concurrent_sessions === 1
              ? "the team's slot is taken until it ends"
              : `${quota.running} of ${quota.max_concurrent_sessions} team slots in use`}
          </span>
        </span>
      </Banner>
    );
  }

  if (quota?.blocked) {
    return (
      <Banner tone="amber" to="/team">
        <Lock className="size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="font-medium">Not open to you yet</span>
          <span className="text-muted-foreground">
            {" · "}
            {challenge.opens_at != null && quota.blocked.startsWith(`${challenge.name} opens `)
              ? `${challenge.name} opens ${formatEasternTimestamp(challenge.opens_at)}. The exploration data and runs are available from then; the challenge page counts down to it.`
              : quota.blocked}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-sm font-medium">
          My team
          <ArrowRight className="size-4" aria-hidden />
        </span>
      </Banner>
    );
  }

  return null;
}

function Banner({
  tone,
  to,
  children,
}: {
  tone: "live" | "muted" | "amber";
  to?: string;
  children: React.ReactNode;
}) {
  const className = cn(
    "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm",
    tone === "live" && "border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors",
    tone === "muted" && "bg-muted/40",
    tone === "amber" &&
      "border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors",
    to && "hover:bg-muted/60",
  );
  return to ? (
    <Link to={to} className={className}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );
}
